// @flow
import React, { Component, Fragment } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';

// Helpers
import classNames from 'classnames';
import isWindow from 'Common/isWindow';
import { gtmEventPush } from 'utils/gtm/events';

import Icon from 'Common/Icon';
import styles from 'components/LightBox.scss';
import Image from 'Common/Image';
import Gallery from 'Common/Gallery';
import Button from 'Common/Button';
import PartnershipLogo from '../PartnershipLogo';
import SimpleModal from 'Common/SimpleModal';
import PriceIndicator from 'Common/PriceIndicator';

/**
 * <LightBox /> component.
 * @return {class} returns LightBox instance
 */
class LightBox extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      imageSrc: this.props.image,
      showPriceRatingInfo: false
    };
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {string} id - LightBox id.
   * @prop {string} name - LightBox name.
   * @prop {number} price - LightBox price.
   * @prop {string} year - LightBox year.
   * @prop {oneOfType} distanceOrTown - LightBox distanceOrTown.
   * @prop {number} mileage - LightBox mileage.
   * @prop {string} fuelType - LightBox fuelType.
   * @prop {string} doors - LightBox doors.
   * @prop {string} transmissionType - LightBox transmission.
   * @prop {string} image - LightBox image.
   * @prop {array} carousel - images list.
   * @prop {func} onClose: handleClick
   * @prop {string} externalUrl: externalUrl
   * @prop {string} attentionGrabber: attentionGrabber
   * @prop {string} priceIndicatorRating: priceIndicatorRating
   * @prop {number} engineSize: engineSize
   * @prop {number} enginePower: enginePower
   *
   */
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    make: PropTypes.string,
    range: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.string,
    distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mileage: PropTypes.number,
    fuelType: PropTypes.string,
    doors: PropTypes.number,
    transmission: PropTypes.string,
    image: PropTypes.object,
    carousel: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    externalUrl: PropTypes.string.isRequired,
    attentionGrabber: PropTypes.string,
    priceIndicatorRating: PropTypes.string,
    engineSize: PropTypes.number,
    enginePower: PropTypes.number
  };

  handleClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    const { item, handleClick, externalUrl } = this.props;
    e.preventDefault();
    handleClick(item);
    isWindow && window.open(externalUrl, '_blank');
  };

  handleClickChild: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Quick look - ${name}`,
        itemCategory: 'Classifieds quick look popup'
      }
    });
    this.handleClick(e);
  };

  handleInfoClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    e.preventDefault();
    const { showPriceRatingInfo } = this.state;
    this.setState({ showPriceRatingInfo: !showPriceRatingInfo }, () => {
      if (isWindow && this.state.showPriceRatingInfo) {
        const scrollPos = document.getElementById('priceIndicatorContainer')
          .offsetTop;
        document.getElementById('simpleModal').scrollTo({
          behavior: 'smooth',
          left: 0,
          top: scrollPos
        });
      }
    });
  };

  /**
   * Renders <LightBox /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render(): Node {
    const {
      name,
      price,
      year,
      distanceOrTown,
      mileage,
      fuelType,
      doors,
      transmission,
      image,
      carousel,
      onClose,
      attentionGrabber,
      priceIndicatorRating,
      enginePower,
      engineSize
    } = this.props;

    const attentionGrabberPoints = attentionGrabber
      ? attentionGrabber.split(', ')
      : [];

    const { showPriceRatingInfo } = this.state;

    const SpecificationList = (
      <ul className={styles.specificationList}>
        {year && (
          <li className={styles.detail} data-fuel-type>
            {year} reg
          </li>
        )}
        {fuelType && (
          <li className={styles.detail} data-fuel-type>
            {fuelType}
          </li>
        )}
        {doors && (
          <li className={styles.detail} data-fuel-type>
            {doors} Doors
          </li>
        )}
        {transmission && (
          <li className={styles.detail} data-fuel-type>
            {transmission !== 'N/A'
              ? transmission[0].toUpperCase() +
                transmission.substring(1).toLowerCase()
              : transmission}
          </li>
        )}
        {mileage && (
          <li className={styles.detail} data-fuel-type>
            {mileage && mileage.toLocaleString('en')} Miles
          </li>
        )}
        {engineSize && (
          <li className={styles.detail} data-fuel-type>
            {engineSize} L
          </li>
        )}
        {enginePower && (
          <li className={styles.detail} data-fuel-type>
            {enginePower} BHP
          </li>
        )}
      </ul>
    );

    const displayButton = (
      <Button
        type={'transparent'}
        rel="nofollow"
        extendStyle={styles.externalButton}
        buttonText={
          <Fragment>
            Full vehicle details
            <Icon
              className={classNames(styles.externalIcon)}
              iconName={'newwindow'}
              size={'default'}
            />
          </Fragment>
        }
        onClick={e => this.handleClickChild(e, name)}
      />
    );
    return (
      <SimpleModal {...this.props} closeIcon={'close'} isModalOpen={true}>
        <div className={styles.container}>
          <button className={styles.closeButton} onClick={onClose}>
            Close
          </button>
          <div className={styles.innerContainer}>
            {carousel && carousel.length ? (
              <Gallery pictures={carousel} />
            ) : null}
            {!image && (
              <div>
                <Image
                  src={`${process.env.PUBLIC_URL || ''}/images/fallback.png`}
                  alt={'Image unavailable'}
                  title={'Image unavailable'}
                />
              </div>
            )}

            <div className={styles.mainDetail}>
              <div className={classNames(styles.section, styles.title)}>
                <h3 className={styles.name}>{name}</h3>
                <div
                  className={classNames(
                    styles.detail,
                    styles.detailDistanceOrTown
                  )}
                >
                  {distanceOrTown && (
                    <Icon
                      className={classNames(
                        styles.icon,
                        styles.iconDistanceOrTown
                      )}
                      iconName={'location'}
                      size={'medium'}
                    />
                  )}
                  {distanceOrTown}
                </div>
              </div>
              <div className={classNames(styles.section, styles.specs)}>
                {SpecificationList}
              </div>
              <div className={classNames(styles.section, styles.priceWrapper)}>
                <div className={styles.price}>
                  <div className={styles.priceIndicatorRating}>
                    {!!priceIndicatorRating &&
                      priceIndicatorRating !== 'NOANALYSIS' && (
                        <div className={styles.priceIndicator}>
                          <Icon
                            iconName={`AT-${priceIndicatorRating}`}
                            size={'large'}
                            className={styles.rating}
                          />
                          <Icon
                            iconName={'info'}
                            size={'medium'}
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              this.setState({
                                showPriceRatingInfo: !showPriceRatingInfo
                              });
                            }}
                            className={styles.info}
                          />
                        </div>
                      )}
                  </div>
                  <div className={styles.priceText}>{price}</div>
                </div>
                <div className={styles.atLink}>{displayButton}</div>
                <div className={styles.attentionGrabberWrapper}>
                  <ul className={styles.attentionGrabber}>
                    {attentionGrabberPoints.map((point, key) => (
                      <li key={`attentionGrabberPoint${key}`}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div className={styles.partnerWrapper}>
                  <PartnershipLogo extendStyle={styles.partnerShipLogo} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {showPriceRatingInfo && (
          <div
            id="priceIndicatorContainer"
            className={styles.priceIndicatorContainer}
          >
            <PriceIndicator />
          </div>
        )}
      </SimpleModal>
    );
  }
}

export default LightBox;



// WEBPACK FOOTER //
// ./src/Common/LightBox/index.js